import {BaseApi} from './BaseApi'

/**
 * Paycode API Service
 *
 * Complete API integration for MSP Platform Paycode management
 *
 * Available Endpoints:
 *
 * READ Operations:
 * - GET /paycode/getPaycodes - Get paginated list of paycodes
 * - GET /paycode/getAllPaycodes - Get all paycodes
 * - GET /paycode/getActivePaycodes - Get all active paycodes
 * - GET /paycode/getPaycode/{id} - Get paycode by ID
 * - GET /paycode/getPaycodesByEor/{eorId} - Get paycodes assigned to a specific EOR
 * - GET /paycode/searchPaycodes - Search paycodes by name, type, date created, and/or shared by (EOR)
 * - POST /paycode/getPaycodeNamesByIds - Get paycode names by IDs
 *
 * WRITE Operations:
 * - POST /paycode/createPaycode - Create a new paycode (Platform Admin only)
 * - PUT /paycode/updatePaycode - Update an existing paycode (Platform Admin only)
 * - PUT /paycode/updatePaycodeEors - Update paycode EOR assignments (Platform Admin only)
 * - PUT /paycode/archivePaycode/{id} - Archive a paycode (Platform Admin only)
 * - PUT /paycode/unarchivePaycode/{id} - Unarchive/reactivate a paycode (Platform Admin only)
 * - DELETE /paycode/deletePaycode/{id} - Delete a paycode (Platform Admin only)
 *
 * ASSIGNMENT Operations:
 * - POST /paycode/assignPaycodesToEor - Assign multiple paycodes to an EOR (Platform Admin only)
 * - POST /paycode/removePaycodesFromEor - Remove multiple paycodes from an EOR (Platform Admin only)
 */

// Enums matching backend
export enum PaycodeType {
  RegularEarnings = 'RegularEarnings',
  Bonus = 'Bonus',
  PermanentDeduction = 'PermanentDeduction',
  TemporaryDeduction = 'TemporaryDeduction',
  Reimbursement = 'Reimbursement',
}

export enum PaycodeStatus {
  Active = 'Active',
  Archived = 'Archived',
}

// New enums for compliance metadata
export enum LegalCategory {
  PreTaxMedical = 'preTaxMedical',
  HSA = 'HSA',
  FSA = 'FSA',
  Retirement401k = 'retirement401k',
  Garnishment = 'garnishment',
  Levy = 'levy',
  UnionDues = 'unionDues',
  PostTaxVoluntary = 'postTaxVoluntary',
}

export enum AllocationStrategy {
  Annualized = 'annualized',
  FlatPerPaycheck = 'flatPerPaycheck',
  ProratedDaily = 'proratedDaily',
}

export enum ShortfallRule {
  DeductAvailable = 'deductAvailable',
  SkipCarryForward = 'skipCarryForward',
  EmployerAbsorbs = 'employerAbsorbs',
  ForceFull = 'forceFull',
}

export enum FinalCheckRule {
  ProrateFinal = 'prorateFinal',
  CollectFullFinal = 'collectFullFinal',
  OnlyIfFundsAvailable = 'onlyIfFundsAvailable',
  DoNotCollectFinal = 'doNotCollectFinal',
}

// Interfaces matching backend DTOs
export interface EorInfo {
  id: string
  name: string
}

export interface Paycode {
  _id: string
  name: string
  description?: string
  systemCode?: string
  type: PaycodeType
  vertexDeductionCode?: string
  vertexPayCode?: string
  w2Box?: string
  w2Label?: string
  allowWebTimeEntry?: boolean
  isDiscretionaryBonus?: boolean
  isNonCashBenefit?: boolean
  isPermanent?: boolean
  isEmployerContribution?: boolean
  isHealthcare?: boolean
  allowMultipleEntries?: boolean
  allowNegativeAmounts?: boolean
  applyRegularRate?: boolean
  includeAsDefaultInBillcode?: boolean
  isAdjustment?: boolean
  isTiered?: boolean
  isPreTax: boolean // Required - one of isPreTax or isPostTax must be true
  isPostTax: boolean // Required - one of isPreTax or isPostTax must be true
  // New compliance metadata fields
  legalCategory?: LegalCategory
  isMonthlyPremium: boolean
  allocationStrategy?: AllocationStrategy
  shortfallRule?: ShortfallRule
  finalCheckRule?: FinalCheckRule
  eorIds: string[]
  eorInfo?: EorInfo[] // New field from backend
  status: PaycodeStatus
  createdBy: string
  modifiedBy: string
  createdDate: string
  modifiedDate: string
}

export interface CreatePaycodeRequest {
  name: string
  description?: string
  systemCode?: string
  type: PaycodeType
  vertexDeductionCode?: string
  vertexPayCode?: string
  w2Box?: string
  w2Label?: string
  allowWebTimeEntry?: boolean
  isDiscretionaryBonus?: boolean
  isNonCashBenefit?: boolean
  isPermanent?: boolean
  isEmployerContribution?: boolean
  isHealthcare?: boolean
  allowMultipleEntries?: boolean
  allowNegativeAmounts?: boolean
  applyRegularRate?: boolean
  includeAsDefaultInBillcode?: boolean
  isAdjustment?: boolean
  isTiered?: boolean
  isPreTax: boolean // Required - one of isPreTax or isPostTax must be true
  isPostTax: boolean // Required - one of isPreTax or isPostTax must be true
  // New compliance metadata fields
  legalCategory?: LegalCategory // Optional per swagger
  isMonthlyPremium: boolean // Required
  allocationStrategy?: AllocationStrategy // Required if isMonthlyPremium is true
  shortfallRule: ShortfallRule // Required per swagger
  finalCheckRule: FinalCheckRule // Required per swagger
  eorIds?: string[]
}

export interface UpdatePaycodeRequest {
  id: string
  name: string
  description?: string
  systemCode?: string
  type: PaycodeType
  vertexDeductionCode?: string
  vertexPayCode?: string
  w2Box?: string
  w2Label?: string
  allowWebTimeEntry?: boolean
  isDiscretionaryBonus?: boolean
  isNonCashBenefit?: boolean
  isPermanent?: boolean
  isEmployerContribution?: boolean
  isHealthcare?: boolean
  allowMultipleEntries?: boolean
  allowNegativeAmounts?: boolean
  applyRegularRate?: boolean
  includeAsDefaultInBillcode?: boolean
  isAdjustment?: boolean
  isTiered?: boolean
  isPreTax: boolean // Required - one of isPreTax or isPostTax must be true
  isPostTax: boolean // Required - one of isPreTax or isPostTax must be true
  // New compliance metadata fields
  legalCategory?: LegalCategory // Optional per swagger
  isMonthlyPremium: boolean // Required
  allocationStrategy?: AllocationStrategy // Required if isMonthlyPremium is true
  shortfallRule: ShortfallRule // Required per swagger
  finalCheckRule: FinalCheckRule // Required per swagger
  status: PaycodeStatus
  // Note: eorIds removed - use updatePaycodeEors endpoint instead
}

export interface UpdatePaycodeEorsRequest {
  paycodeId: string
  eorIds: string[]
}

export interface PaycodePagingModel {
  data: Paycode[]
  totalCount: number
  pageSize: number
  currentPage: number
  totalPages: number
}

export interface PaycodeNameIdModel {
  _id: string
  name: string
}

export interface PagingParametersDto {
  pageSize?: number
  currentPage?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  searchTerm?: string
}

export interface PaycodeSearchParams {
  searchTerm?: string
  type?: PaycodeType | PaycodeType[] // Support single or multiple types
  dateCreated?: string
  eorId?: string
  status?: PaycodeStatus
}

// API Service Class
class PaycodeApiService extends BaseApi {
  private readonly baseUrl = '/paycode'

  // GET /paycode/getPaycodes
  async getPaycodes(params?: PagingParametersDto): Promise<PaycodePagingModel> {
    const queryParams = new URLSearchParams()
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString())
    if (params?.currentPage) queryParams.append('currentPage', params.currentPage.toString())
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm)

    const url = `${this.baseUrl}/getPaycodes${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`
    const response = await this.client.get<PaycodePagingModel>(url)
    return response.data
  }

  // GET /paycode/getAllPaycodes
  async getAllPaycodes(): Promise<Paycode[]> {
    const response = await this.client.get<Paycode[]>(`${this.baseUrl}/getAllPaycodes`)
    return response.data
  }

  // GET /paycode/getActivePaycodes
  async getActivePaycodes(): Promise<Paycode[]> {
    const response = await this.client.get<Paycode[]>(`${this.baseUrl}/getActivePaycodes`)
    return response.data
  }

  // GET /paycode/getPaycode/:id
  async getPaycodeById(id: string): Promise<Paycode> {
    const response = await this.client.get<Paycode>(`${this.baseUrl}/getPaycode/${id}`)
    return response.data
  }

  // GET /paycode/getPaycodesByEor/:eorId
  async getPaycodesByEor(eorId: string): Promise<Paycode[]> {
    const response = await this.client.get<Paycode[]>(`${this.baseUrl}/getPaycodesByEor/${eorId}`)
    return response.data
  }

  // GET /paycode/searchPaycodes
  async searchPaycodes(params: PaycodeSearchParams): Promise<Paycode[]> {
    const queryParams = new URLSearchParams()
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm)
    // Handle multiple types
    if (params.type) {
      if (Array.isArray(params.type)) {
        params.type.forEach((t) => queryParams.append('type', t))
      } else {
        queryParams.append('type', params.type)
      }
    }
    if (params.dateCreated) queryParams.append('dateCreated', params.dateCreated)
    if (params.eorId) queryParams.append('eorId', params.eorId)
    if (params.status) queryParams.append('status', params.status)

    const url = `${this.baseUrl}/searchPaycodes${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`
    const response = await this.client.get<Paycode[]>(url)
    return response.data
  }

  // POST /paycode/getPaycodeNamesByIds
  async getPaycodeNamesByIds(paycodeIds: string[]): Promise<PaycodeNameIdModel[]> {
    const response = await this.client.post<PaycodeNameIdModel[]>(
      `${this.baseUrl}/getPaycodeNamesByIds`,
      paycodeIds,
    )
    return response.data
  }

  // POST /paycode/createPaycode
  async createPaycode(paycode: CreatePaycodeRequest): Promise<Paycode> {
    const response = await this.client.post<Paycode>(`${this.baseUrl}/createPaycode`, paycode)
    return response.data
  }

  // PUT /paycode/updatePaycode
  async updatePaycode(paycode: UpdatePaycodeRequest): Promise<Paycode> {
    const response = await this.client.put<Paycode>(`${this.baseUrl}/updatePaycode`, paycode)
    return response.data
  }

  // PUT /paycode/updatePaycodeEors
  async updatePaycodeEors(request: UpdatePaycodeEorsRequest): Promise<Paycode> {
    const response = await this.client.put<Paycode>(`${this.baseUrl}/updatePaycodeEors`, request)
    return response.data
  }

  // PUT /paycode/archivePaycode/:id
  async archivePaycode(id: string): Promise<Paycode> {
    const response = await this.client.put<Paycode>(`${this.baseUrl}/archivePaycode/${id}`)
    return response.data
  }

  // PUT /paycode/unarchivePaycode/:id
  async unarchivePaycode(id: string): Promise<Paycode> {
    const response = await this.client.put<Paycode>(`${this.baseUrl}/unarchivePaycode/${id}`)
    return response.data
  }

  // DELETE /paycode/deletePaycode/:id
  async deletePaycode(id: string): Promise<Paycode> {
    const response = await this.client.delete<Paycode>(`${this.baseUrl}/deletePaycode/${id}`)
    return response.data
  }

  // POST /paycode/assignPaycodesToEor
  async assignPaycodesToEor(paycodeIds: string[], eorId: string): Promise<void> {
    const response = await this.client.post<void>(`${this.baseUrl}/assignPaycodesToEor`, {
      paycodeIds,
      eorId,
    })
    return response.data
  }

  // POST /paycode/removePaycodesFromEor
  async removePaycodesFromEor(paycodeIds: string[], eorId: string): Promise<void> {
    const response = await this.client.post<void>(`${this.baseUrl}/removePaycodesFromEor`, {
      paycodeIds,
      eorId,
    })
    return response.data
  }
}

// Export singleton instance
export const paycodeApi = new PaycodeApiService()
export default paycodeApi
